import {
  useCallback,
  useMemo,
  useRef,
  useState,
  memo,
  Suspense,
  ReactNode,
  use,
} from 'react';
import _, { isEmpty, isFunction } from 'lodash';
import { ErrorBoundary } from 'react-error-boundary';
import AutoSizer from 'react-virtualized/dist/commonjs/AutoSizer';
import { QueryError } from '../query-error';
import VizChartError from '../VizChartError';
import { LegendAwareChartPlaceholder } from './chart-placeholder.component';
import { LegendAwareChartValidationPanel } from './chart-validation-panel.component';
import { Chart, LegacyChart } from './chart.component';
import {
  useVizChartState,
  VizContextProvider,
  ChartWindowContext,
  useChartValidation,
  VizContext,
} from './viz-chart.hook';
import {
  useHiddenFieldWarnings,
  useOpenDiscoveryPresentStateSelector,
} from '../../common/redux/selectors/viz-selector.hook';
import { ReQuery } from './requery.component';
import { LabelManagerProvider } from '../../common/d3/label-manager-provider';
import { MissingFieldPlaceholder } from './missing-field-placeholder';
import { ExportToExcelPlaceholder } from './export-to-excel-placeholder.component';
import { LogErrorBoundary } from '../../components/error-boundary';
import { SkeletonVizLoader } from '../../common/loaders/skeleton-viz-loader';
import { useIsDashletMode } from '../../auth';
import {
  VizPromiseContext,
  VizPromiseProvider,
  RequeryProvider,
  RequeryContext,
} from '../viz-query/';
import { VizQueryPromiseData } from '../../common/redux/actions/DiscoverActions';

export const VizChart = memo<{
  vizId: string;
  width?: string | number;
  height?: string | number;
}>(({ vizId, width = undefined, height = undefined }) => (
  <ErrorBoundary
    key={vizId}
    fallbackRender={VizChartError}
    onError={LogErrorBoundary}
  >
    <VizContextProvider key={vizId} vizId={vizId}>
      <RequeryProvider>
        <VizPromiseProvider key={vizId}>
          <ChartWindow key={vizId} width={width} height={height}>
            <ActionableChartContent key={vizId} />
          </ChartWindow>
        </VizPromiseProvider>
      </RequeryProvider>
    </VizContextProvider>
  </ErrorBoundary>
));

/**
 * ActionableChartContent - renders viz or any other interactive component when
 * viz should not be rendered
 */
const ActionableChartContent = memo(() => {
  const viz = use(VizContext);
  const discovery = useOpenDiscoveryPresentStateSelector({
    discoveryId: viz?.id,
  });

  const { valid: isValid = false } = useChartValidation();

  const { isBlocked: hasHiddenFields } = useHiddenFieldWarnings({
    discoveryId: viz?.id,
  });

  const hasMissingFields = useMemo(
    () =>
      !_.isEmpty(discovery?.missingFields) ||
      !_.isEmpty(discovery?.missingFilters),
    [discovery?.missingFields, discovery?.missingFilters],
  );

  const { showReQuery, reset: resetHideLiveQueryWarning } = use(RequeryContext);

  // early returns before the suspending chart
  if (hasHiddenFields || !isValid) {
    return hasHiddenFields || hasMissingFields ? (
      <MissingFieldPlaceholder
        key={'MissingFieldPlaceholder'}
        vizId={viz?.id}
      />
    ) : (
      <LegendAwareChartPlaceholder
        key={'LegendAwareChartPlaceholder'}
        vizId={viz?.id}
      />
    );
  }

  if (showReQuery) {
    return <ReQuery key={'ReQuery'} onRefresh={resetHideLiveQueryWarning} />;
  }

  return !showReQuery && <ChartWithQueryWatch />;
});

export const ChartWithQueryWatch = memo(() => {
  // the promise must be in a parent, non-suspended, component because
  // suspended children are always re-rendered. Otherwise, we'll have an infinite loop
  const { promise: vizQueryPromise } = use(VizPromiseContext);

  return (
    <Suspense fallback={<SkeletonVizLoader />}>
      <SuspendingVizChart
        key={'SuspendingVizChart'}
        promise={vizQueryPromise}
      />
    </Suspense>
  );
});

export const SuspendingVizChart = memo<{
  promise: Promise<VizQueryPromiseData>;
}>(({ promise }) => {
  // Use state and callback ref to ensure chartContainer is set after render
  const [vizChartDOM, setVizChartDOM] = useState<HTMLDivElement | null>(null);
  const vizChartRefCallback = useCallback((node: HTMLDivElement | null) => {
    if (node) {
      setVizChartDOM(node);
    }
  }, []);
  const isDashletMode = useIsDashletMode();

  // suspends this component when awaiting/loading query
  const { primaryData, secondaryData } = use(promise) ?? {};

  if (isEmpty(primaryData)) {
    return;
  }

  return (
    <LabelManagerProvider chartContainer={vizChartDOM}>
      <div
        ref={vizChartRefCallback}
        style={
          isDashletMode
            ? {
                height: '100%',
                width: '100%',
              }
            : {}
        }
        className={'viz-chart-container'}
      >
        <Chart
          key='chart'
          queryResults={primaryData}
          secondaryQueryResults={secondaryData}
        />
      </div>
    </LabelManagerProvider>
  );
});

export const LegacyVizChartUnMemoized = ({
  vizId,
  width: overrideWidth = 0,
  height: overrideHeight = 0,
  isIsolated = false,
}) => {
  const {
    useLiveQuery,
    liveQueryHasChanged,
    chartSpec,
    validation,
    hasChanged,
    forceQuery,
    vizLoading: _vizLoading,
    queryResults,
    shouldRunInitialQuery,
    secondaryQueryResults,
    queryError,
    viz,
    isAdvancedMode,
    dirty,
    hasMissingFields,
    hasHiddenFields,
    isBlocked,
  } = useVizChartState({ vizId });
  const showRequery =
    dirty &&
    hasChanged &&
    !useLiveQuery &&
    !liveQueryHasChanged &&
    validation.valid;
  const { id: chartSpecId } = chartSpec ?? {};
  const isFirstRender = useRef(true);
  const [vizChartDOM, setVizChartDOM] = useState();
  const vizChartRefCallback = useCallback(node => {
    // triggers update and sets chart DOM
    if (!_.isNil(node)) {
      setVizChartDOM(node);
    }
  }, []);

  // Don't remove this. We need this component to handle lifecycle events, displaying CorbotoLoading alone breaks things
  const vizLoading =
    !isBlocked &&
    (_vizLoading ||
      (_.isUndefined(_vizLoading) &&
        isFirstRender.current === true &&
        shouldRunInitialQuery));
  isFirstRender.current = false;
  const style: any = {};
  if (isIsolated) {
    style.height = '100%';
    style.width = '100%';
  }
  const chart = useMemo(() => {
    // Neither a stale query error nor cached results may show while hidden fields block the query.
    if (hasHiddenFields) {
      return <MissingFieldPlaceholder vizId={vizId} validation={validation} />;
    }
    if (showRequery) {
      return <ReQuery onRefresh={() => forceQuery()} />;
    }
    if (!validation.valid && isAdvancedMode) {
      // only shown in advanced-mode with errors present
      return (
        <LegendAwareChartValidationPanel
          vizId={vizId}
          validation={validation}
          advanced={isAdvancedMode}
        />
      );
    } else if (!_.isNil(queryError)) {
      return (
        <QueryError queryError={queryError} isAdvancedMode={isAdvancedMode} />
      );
    } else if (!validation.valid || _.isEmpty(queryResults)) {
      return !hasMissingFields ? (
        <LegendAwareChartPlaceholder vizId={vizId} validation={validation} />
      ) : (
        <MissingFieldPlaceholder vizId={vizId} validation={validation} />
      );
    } else if (queryResults && !queryResults.loading) {
      const isOverrideDimensions =
        _.isNumber(overrideWidth) &&
        _.isNumber(overrideHeight) &&
        overrideWidth !== 0 &&
        overrideHeight !== 0;

      if (
        isFunction(chartSpec?.shouldDeferToExport) &&
        chartSpec.shouldDeferToExport(queryResults)
      ) {
        return (
          <ExportToExcelPlaceholder
            vizId={viz?.id}
            queryResults={queryResults}
          />
        );
      }

      return (
        <div
          style={{
            height: overrideHeight || '100%',
            width: overrideWidth || '100%',
          }}
        >
          {isOverrideDimensions ? (
            <div
              style={{
                height: overrideHeight,
                width: overrideWidth,
              }}
            >
              <ErrorBoundary
                fallbackRender={VizChartError}
                onError={LogErrorBoundary}
              >
                {chartSpecId && (
                  <LegacyChart
                    key='chart'
                    spec={chartSpec}
                    viz={viz}
                    vizId={vizId}
                    queryResults={queryResults}
                    secondaryQueryResults={secondaryQueryResults}
                    height={overrideHeight}
                    width={overrideWidth}
                  />
                )}{' '}
              </ErrorBoundary>
            </div>
          ) : (
            <AutoSizer>
              {({ width, height }) => (
                <div
                  style={{
                    height: overrideHeight || height,
                    width: overrideWidth || width,
                  }}
                >
                  <ErrorBoundary
                    fallbackRender={VizChartError}
                    onError={LogErrorBoundary}
                  >
                    {height > 0 && width > 0 && (
                      <LegacyChart
                        key='chart'
                        spec={chartSpec}
                        viz={viz}
                        vizId={vizId}
                        queryResults={queryResults}
                        secondaryQueryResults={secondaryQueryResults}
                        height={height}
                        width={width}
                      />
                    )}
                  </ErrorBoundary>
                </div>
              )}
            </AutoSizer>
          )}
        </div>
      );
    } else {
      // catch-all, if all else fails show the placeholder. but we really should never get here.
      return (
        <LegendAwareChartPlaceholder vizId={vizId} validation={validation} />
      );
    }
  }, [
    hasHiddenFields,
    showRequery,
    validation,
    isAdvancedMode,
    queryError,
    queryResults,
    forceQuery,
    vizId,
    hasMissingFields,
    overrideWidth,
    overrideHeight,
    chartSpec,
    chartSpecId,
    viz,
    secondaryQueryResults,
  ]);
  return vizLoading ? (
    <SkeletonVizLoader />
  ) : (
    <div
      ref={vizChartRefCallback}
      style={style}
      className={'viz-chart-container'}
    >
      <LabelManagerProvider chartContainer={vizChartDOM}>
        {chart}
      </LabelManagerProvider>
    </div>
  );
};

// This is carried over from legacy
export const ChartWindow = ({
  width: overrideWidth = 0,
  height: overrideHeight = 0,
  children,
}: {
  width: string | number;
  height: string | number;
  children: ReactNode;
}) => {
  const isOverrideDimensions =
    _.isNumber(overrideWidth) &&
    _.isNumber(overrideHeight) &&
    overrideWidth !== 0 &&
    overrideHeight !== 0;

  return (
    <div
      style={{
        height: overrideHeight || '100%',
        width: overrideWidth || '100%',
      }}
    >
      {isOverrideDimensions ? (
        <div
          style={{
            height: overrideHeight,
            width: overrideWidth,
          }}
        >
          <ChartWindowContext.Provider
            value={{
              height: overrideHeight,
              width: overrideWidth,
            }}
          >
            {children}
          </ChartWindowContext.Provider>
        </div>
      ) : (
        <AutoSizer>
          {({ width, height }) => (
            <div
              style={{
                height: overrideHeight || height,
                width: overrideWidth || width,
              }}
            >
              <ChartWindowContext.Provider
                value={{
                  height,
                  width,
                }}
              >
                {children}
              </ChartWindowContext.Provider>
            </div>
          )}
        </AutoSizer>
      )}
    </div>
  );
};
